'use client';

import { useEffect, useState } from 'react';
import { DATA_NAMES } from '../constants';

export interface LayoutPositions {
  navbarHeight: number;
  bannerOffset: number;
  totalOffset: number;
}

export function useLayoutPosition() {
  const [positions, setPositions] = useState<LayoutPositions>({
    navbarHeight: 0,
    bannerOffset: 0,
    totalOffset: 0,
  });

  useEffect(() => {
    const navbarElement = document.querySelector(
      `[data-name="${DATA_NAMES.NAVBAR}"]`
    );
    const announcementElement = document.querySelector(
      `[data-name="${DATA_NAMES.ANNOUNCEMENT_BANNER}"]`
    );

    if (!navbarElement) return;

    // Get navbar height once since it doesn't change
    const navbarHeight = navbarElement.getBoundingClientRect().height;

    // Function to calculate banner visibility precisely
    const calculateBannerOffset = () => {
      if (!announcementElement) return 0;

      const rect = announcementElement.getBoundingClientRect();
      return Math.min(rect.height, Math.max(0, rect.bottom));
    };

    // Function to update positions based on banner visibility
    const updatePositions = () => {
      const bannerOffset = calculateBannerOffset();
      const totalOffset = navbarHeight + bannerOffset;

      // Set CSS variables
      document.documentElement.style.setProperty(
        '--navbar-height',
        `${navbarHeight}px`
      );
      document.documentElement.style.setProperty(
        '--banner-offset',
        `${bannerOffset}px`
      );
      document.documentElement.style.setProperty(
        '--total-offset',
        `${totalOffset}px`
      );

      setPositions({ navbarHeight, bannerOffset, totalOffset });
    };

    // Initial update
    updatePositions();

    // If there's no announcement banner, we're done
    if (!announcementElement) return;

    // For pixel-perfect tracking, we need to use scroll
    // But optimize with requestAnimationFrame
    let ticking = false;
    const scrollHandler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updatePositions();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Use passive event listener for better performance
    window.addEventListener('scroll', scrollHandler, { passive: true });

    // Add resize listener for rare cases (orientation change, etc)
    window.addEventListener('resize', updatePositions, { passive: true });

    return () => {
      window.removeEventListener('scroll', scrollHandler);
      window.removeEventListener('resize', updatePositions);
    };
  }, []);

  return positions;
}
